/**
 * Where a scanned setup code lands (DECISIONS D-013).
 *
 * `claimOnboardingFromLocation()` (called in src/main.tsx before anything
 * renders) has already taken the secret out of the URL and rewritten the
 * address bar, so by the time this screen exists the code is not in the
 * address bar, not in history, and not in a screenshot of either.
 *
 * What is left is the part that can fail: exchanging the code for the
 * surveyor's access token, storing it, and saying something true about the
 * result. The four outcomes are not interchangeable and the difference is the
 * whole reason this is a screen rather than a redirect:
 *
 *   linked   — the phone is theirs now. One big button out.
 *   refused  — the code was used, expired, or replaced. The office fixes it,
 *              and the sentence says so; there is no Try Again, because trying
 *              again is exactly what cannot help with a single-use code.
 *   offline  — the request never landed. NOTHING was spent, the code on the
 *              office screen still works, and Try Again is the right control.
 *   nothing  — a reload, or somebody navigating here by hand.
 *
 * ── Redeemed once per page load, and not by this effect ─────────────────────
 * The redemption is memoised in `onboardingClaim.ts`, outside React. StrictMode
 * runs effects twice in development; a redemption started here would fire two
 * requests and the second would be refused as `onboarding_link_used`, so a
 * working code would report itself as already spent. Every call here awaits the
 * same promise.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { hrefFor } from '../routing';
import { useServices } from '../state/services';
import { OnboardingError } from '../../db/onboarding-client';
import { hasPendingOnboarding, redeemPendingOnboarding } from '../state/onboardingClaim';
import { onboardView, type OnboardPhase } from '../state/onboardingView';

interface Failure {
  code: string;
  message: string;
}

export function OnboardScreen(): ReactNode {

  const { sync } = useServices();
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<OnboardPhase>(() =>
    hasPendingOnboarding() ? 'redeeming' : 'nothing',
  );
  const [surveyorName, setSurveyorName] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<Failure | null>(null);

  useEffect(() => {
    if (!hasPendingOnboarding()) {
      setPhase('nothing');
      return undefined;
    }
    setPhase('redeeming');

    let cancelled = false;
    void redeemPendingOnboarding()
      .then((result) => {
        if (cancelled) return;
        setSurveyorName(result.surveyor.name);
        setFailure(null);
        setPhase('linked');
        // The office assigned work before they had a phone to receive it on —
        // that is the whole sequence this feature unblocks — so pull now rather
        // than at the next 30-second tick. This is the one moment we know for
        // certain there is both a token and a signal.
        void sync
          .pullAssignments()
          .catch(() => undefined)
          .then(() => sync.drain())
          .catch(() => undefined);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const code = error instanceof OnboardingError ? error.code : 'unknown';
        const message = error instanceof Error ? error.message : '';
        setFailure({ code, message });
        // Offline is NOT a refusal. Nothing was spent, so the code on the
        // office screen still works and Try Again is honest.
        setPhase(code === 'offline' ? 'offline' : 'refused');
      });

    return () => {
      cancelled = true;
    };
  }, [sync, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const view = onboardView({
    phase,
    ...(surveyorName ? { surveyorName } : {}),
    ...(failure ? { errorCode: failure.code, errorMessage: failure.message } : {}),
  });

  return (
    <>
      {/* aria-live: the phase changes after an async call, so a screen-reader
          user is told the outcome instead of being left on "Setting up…". */}
      <div className="card" role="status" aria-live="polite">
        <p className="q" data-legibility="question">
          {view.headline}
        </p>
        <p className="ruleline" data-legibility="helper">
          {view.detail}
        </p>
      </div>

      {view.phase === 'refused' ? (
        <p className="empty">
          Each setup code works once and lasts a day. Nothing you have already captured on this
          phone is affected.
        </p>
      ) : null}

      <div className="navbar">
        {view.canProceed ? (
          <a className="btn btn--primary" href={hrefFor({ name: 'home' })}>
            My surveys
          </a>
        ) : null}

        {view.phase === 'offline' ? (
          <button type="button" className="btn btn--ghost" onClick={retry}>
            Try again
          </button>
        ) : null}

        {view.phase === 'refused' || view.phase === 'nothing' ? (
          <a className="btn btn--ghost" href={hrefFor({ name: 'home' })}>
            My surveys
          </a>
        ) : null}
      </div>
    </>
  );
}
